import { createApi } from '@reduxjs/toolkit/query/react'
import { sendNotification } from 'components'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { GenericResponse, TableRequestV2, TableResponseV2 } from 'services/types'
import { DropdownOptionTemplate } from 'types/common'
import {
  AddDocumentConfigurationRequest,
  WasteNoteSchemaField,
  GetDocumentCongigurationDetailResponse,
  MutationResponse,
} from './types'

export const opsDocumentApi = createApi({
  reducerPath: 'opsDocumentApi',
  tagTypes: [
    'Documents',
    'DocumentConfigurations',
    'DocumentConfigurationDetail',
    'AvailableOperations',
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/api/'}`,
  }),
  endpoints: (builder) => ({
    getOpsDocumentList: builder.query<DropdownOptionTemplate<number>, number>({
      query: (clientId) => ({
        url: `clients/${clientId}/ops_management/client_documents/`,
        method: 'GET',
      }),
      providesTags: ['Documents'],
    }),
    createDocument: builder.mutation<
      any, // TODO: Define the correct type for the response after BACKEND development
      {
        clientId: number
        form_type_id: number
        object_id: number
        environment: string
        model_name: 'route'
      }
    >({
      query: ({ clientId, ...body }) => ({
        url: `clients/${clientId}/ops_management/document/`,
        method: 'POST',
        data: { ...body, app_label: 'evrekaTest' },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          console.log(err)
        }
      },
      invalidatesTags: ['Documents'],
    }),
    getDocumentConfigurationList: builder.query<TableResponseV2<{}>, TableRequestV2<number>>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/ops_management/document_configurations/`,
        method: 'POST',
        data,
      }),
      providesTags: ['DocumentConfigurations'],
    }),
    deleteDocumentConfiguration: builder.mutation<
      GenericResponse,
      { clientId: number; id: number }
    >({
      query: ({ clientId, id }) => ({
        url: `clients/${clientId}/ops_management/document_configuration/${id}/`,
        method: 'DELETE',
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
      invalidatesTags: ['DocumentConfigurations', 'AvailableOperations'],
    }),
    getDocumentSchema: builder.query<
      {
        schema: Record<number, Array<WasteNoteSchemaField>>
        sections: DropdownOptionTemplate<number>
      },
      { clientId: number; formTypeId: number }
    >({
      query: ({ clientId, formTypeId }) => ({
        url: `clients/${clientId}/ops_management/client_documents/${formTypeId}/schema/`,
        method: 'GET',
      }),
    }),
    addDocumentConfiguration: builder.mutation<
      GenericResponse & { id: number },
      AddDocumentConfigurationRequest
    >({
      query: ({ clientId, ...body }) => ({
        url: `clients/${clientId}/ops_management/document_configuration/`,
        method: 'POST',
        data: body,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
      invalidatesTags: ['DocumentConfigurations', 'AvailableOperations'],
    }),
    editDocumentConfiguration: builder.mutation<
      GenericResponse,
      AddDocumentConfigurationRequest & { id: number }
    >({
      query: ({ id, clientId, ...body }) => ({
        url: `clients/${clientId}/ops_management/document_configuration/${id}/`,
        method: 'PUT',
        data: body,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
      invalidatesTags: [
        'DocumentConfigurations',
        'DocumentConfigurationDetail',
        'AvailableOperations',
      ],
    }),
    getDocumentConfiguration: builder.query<
      GetDocumentCongigurationDetailResponse,
      { clientId: number; documentConfigurationId: number }
    >({
      query: ({ clientId, documentConfigurationId }) => ({
        url: `clients/${clientId}/ops_management/document_configuration/${documentConfigurationId}/`,
        method: 'GET',
      }),
      providesTags: ['DocumentConfigurationDetail'],
    }),
    editAttachments: builder.mutation<
      MutationResponse,
      { clientId: number; document_configuration_id: number; body: FormData }
    >({
      query: ({ clientId, document_configuration_id, body }) => ({
        url: `clients/${clientId}/ops_management/document_configuration/${document_configuration_id}/edit_attachments/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['DocumentConfigurationDetail', 'DocumentConfigurations'],
    }),
    getAvailableOperations: builder.query<
      { operations: DropdownOptionTemplate<number> },
      { clientId: number }
    >({
      query: ({ clientId }) => ({
        url: `clients/${clientId}/ops_management/document/available_operations/`,
        method: 'GET',
      }),
      providesTags: ['AvailableOperations'],
    }),
  }),
})

export const {
  useGetOpsDocumentListQuery,
  useCreateDocumentMutation,
  useGetDocumentConfigurationListQuery,
  useDeleteDocumentConfigurationMutation,
  useGetDocumentSchemaQuery,
  useAddDocumentConfigurationMutation,
  useGetDocumentConfigurationQuery,
  useEditDocumentConfigurationMutation,
  useEditAttachmentsMutation,
  useGetAvailableOperationsQuery,
} = opsDocumentApi
